import React from "react";
import styled, { css } from "styled-components";
import DatePicker from "react-datepicker";
import { connect, type MapStateToProps } from "react-redux";
import Gettext, { inferMomentLocale } from "../common/Gettext";
import { Flex, Header } from "./shared-styles";
import "react-datepicker/dist/react-datepicker.css";
import { fontSize, rtl } from "../common/styled-mixins";

import type { State as ReduxState } from "../../redux/reducer/index";

const Container = styled(Flex)`
  direction: ${({ theme }) => theme.direction};
  justify-content: flex-start;
  align-content: flex-start;

  @media (min-width: 960px) {
    max-width: ${props => (props.headerWidth ? "322px" : "auto")};
  }

  @media (max-width: 959px) {
    flex: 1 0 auto;
    margin-right: 6px;
  }

  @media (max-width: 414px) {
    margin-right: 4px;
  }
  @media (max-width: 360px) {
    margin: 5px auto;
  }
`;

const Separator = styled.span`
  height: 2px;
  width: 16px;
  margin: 0 10px;
  background-color: #b5b5b5;

  @media (max-width: 479px) {
    display: none;
  }
`;

const DateInput = styled.button`
  background: none;
  max-width: 139px;
  height: 40px;
  padding: 0 39px 0 11px;
  font-size: 16px;
  color: ${({ theme }) =>
    theme.calendarInputColor
      ? theme.calendarInputColor + " !important"
      : "#4e2a81"};
  border: 1px solid #cccccc;
  border-radius: 4px;
  ${({ theme }) =>
    theme.calendarBackground
      ? css`
          background-color: ${theme.mainDark};
        `
      : css`
          box-shadow: inset 0 2px 0 0 #ebebeb;
        `};
  cursor: default;

  @media (max-width: 959px) {
    max-width: 8em;
    height: 2.25em;
    padding-left: 0.625em;
    padding-right: 2.125em;
    font-size: 16px;
  }

  @media (max-width: 640px) {
    font-size: ${fontSize(14)};
  }

  @media (max-width: 414px) {
    max-width: 5.786em;
    padding: 0 4px;
    font-size: 16px;
  }

  @media (max-width: 360px) {
    font-size: ${fontSize(14)};
  }
`;

const PickerView = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
  font-weight: 400;
  background: #fff;

  &:not(:last-of-type) {
    @media (max-width: 959px) {
      ${rtl("margin-right", "6px")};
    }

    @media (max-width: 479px) {
      ${rtl("margin-right", "4px")};
    }
  }
`;

const Icon = styled.svg`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 21px;
  height: 20px;
  cursor: pointer;
  pointer-events: none;

  @media (max-width: 959px) {
    right: 6px;
  }

  @media (max-width: 479px) {
    display: none;
  }
`;

function _Picker(props) {
  return (
    <PickerView>
      <DatePicker dateFormat="dd.MM.yyyy" maxDate={new Date()} {...props} />
      <Icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 20">
        <path
          fillRule="evenodd"
          fill="rgb(165, 164, 164)"
          d="M0.000,20.000 L0.000,2.026 L4.015,2.026 L4.015,-0.001 L5.985,-0.001 L5.985,2.026 L15.005,2.026 L15.005,-0.001 L17.006,-0.001 L17.006,2.025 L21.014,2.025 L21.014,20.000 L0.000,20.000 ZM19.002,3.972 L17.006,3.972 L17.006,4.987 L15.005,4.987 L15.005,3.972 L5.985,3.972 L5.985,4.987 L4.015,4.987 L4.015,3.972 L1.998,3.972 L1.998,17.957 L19.002,17.957 L19.002,3.972 ZM6.989,10.962 L3.988,10.962 L3.988,7.995 L6.989,7.995 L6.989,10.962 ZM6.989,15.962 L3.988,15.962 L3.988,12.995 L6.989,12.995 L6.989,15.962 ZM11.989,10.962 L8.988,10.962 L8.988,7.995 L11.989,7.995 L11.989,10.962 ZM11.989,15.962 L8.988,15.962 L8.988,12.995 L11.989,12.995 L11.989,15.962 ZM16.989,10.962 L13.988,10.962 L13.988,7.995 L16.989,7.995 L16.989,10.962 ZM16.989,15.962 L13.988,15.962 L13.988,12.995 L16.989,12.995 L16.989,15.962 Z"
        />
      </Icon>
    </PickerView>
  );
}

const mapStateToProps = (state: ReduxState) => ({
  locale: inferMomentLocale(state)
});

const Picker = connect((mapStateToProps: MapStateToProps<*, *, *>))(_Picker);

const CustomInputToDisableMobileKeyboards = ({ value, onClick }) => (
    <DateInput onClick={onClick}>
      {value}
    </DateInput>
  );

const Period = ({
  windowWidth,
  headerWidth,
  startDate,
  endDate,
  updateState
}) => (
  <Container headerWidth={headerWidth}>
    <Header headerWidth={headerWidth}>
      <Gettext>Period</Gettext>:
    </Header>
    <Picker
      withPortal={windowWidth <= 959}
      className="needsclick"
      selected={startDate.valueOf()}
      selectsStart
      startDate={startDate.valueOf()}
      endDate={endDate.valueOf()}
      onChange={value =>
        updateState({
          key: "startDate",
          value
        })
      }
      customInput={<CustomInputToDisableMobileKeyboards />}
    />
    <Separator />
    <Picker
      withPortal={windowWidth <= 959}
      className="needsclick"
      selected={endDate.valueOf()}
      selectsEnd
      startDate={startDate.valueOf()}
      endDate={endDate.valueOf()}
      onChange={value =>
        updateState({
          key: "endDate",
          value
        })
      }
      popoverAttachment={"top right"}
      popoverTargetAttachment={"bottom right"}
      customInput={<CustomInputToDisableMobileKeyboards />}
    />
  </Container>
);

export default Period;



// WEBPACK FOOTER //
// ./src/components/HistoryPages/Period.js